import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BookMarked, FileCheck2, GitCompareArrows } from 'lucide-react';

import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import AuthModal from '@/components/auth/AuthModal';
import ManuscriptProof, { ProofScene } from '@/components/writer/guest/ManuscriptProof';
import SeeHowItWorks from '@/components/writer/guest/SeeHowItWorks';
import WriterStage from '@/components/writer/WriterStage';
import PromptComposer from '@/components/writer/guest/PromptComposer';
import { SITE_URL } from '@/lib/constants/urls';
import { useWriterPromptStart } from '@/hooks/useWriterPromptStart';

/**
 * Guest landing for /write (prompt-first, no empty editor).
 *
 * The page *is* the composer: one lit surface centred on a quiet paper field,
 * with the manuscript assembling directly beneath it so prompt and paper read
 * as one object — you type at the top, the page forms underneath. Hovering a
 * starter chip jumps the page to the scene that answers it; once the visitor
 * focuses or types, the animation settles on its finished, compiled state.
 * On mobile the page card is dropped for a one-line strip above the fold.
 *
 * Nothing is compiled, no project row is written and no credits are touched
 * before authentication — the typed prompt is stored as a pending intent and
 * replayed by WriterHome / useWriterThread after sign-in.
 */

const CAPABILITIES = [
  { icon: FileCheck2, title: 'Compiled PDF' },
  { icon: BookMarked, title: 'Real citations' },
  { icon: GitCompareArrows, title: 'Tracked revisions' },
];

const CHIP_TO_SCENE: Record<string, ProofScene> = {
  'Add citations to my introduction': 'literature',
  'Draft a related-work section': 'write',
  'Turn my outline into a paper': 'write',
  'Tighten my abstract to 150 words': 'revise',
};

const WriterGuestLanding: React.FC = () => {
  const [chipIntent, setChipIntent] = useState<ProofScene | null>(null);
  const [composerActive, setComposerActive] = useState(false);
  const [replayToken, setReplayToken] = useState(0);
  const [hintDismissed, setHintDismissed] = useState(false);
  const proofRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const {
    authOpen,
    setAuthOpen,
    submitPrompt,
    continueAfterAuth,
    preserveOAuthDestination,
  } = useWriterPromptStart();

  // The hint did its job once clicked; it only comes back when the visitor
  // returns to the top of the page.
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY < 100) setHintDismissed(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSeeHowItWorks = () => {
    setHintDismissed(true);
    // Replay from the first scene, so "how it works" is told from the start
    // instead of landing mid-cycle.
    setReplayToken((n) => n + 1);
    proofRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  };

  const rise = (delay: number) =>
    prefersReducedMotion
      ? { initial: false as const, animate: { opacity: 1, y: 0 } }
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.5, ease: 'easeOut' as const },
        };

  const handleChipIntent = (chip: string | null) => {
    setChipIntent(chip ? CHIP_TO_SCENE[chip] ?? null : null);
  };

  return (
    <>
      <Helmet>
        <title>PaperVerse Writer — Write your paper with an AI co-author</title>
        <meta
          name="description"
          content="Describe your paper and PaperVerse Writer drafts it in LaTeX, compiles it to a clean PDF, and resolves real citations. Free account, private until you publish."
        />
        <link rel="canonical" href={`${SITE_URL}/write`} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="relative flex-1 w-full overflow-hidden">
          {/* Background: ruled paper field, pooled light, two calm orbs */}
          <WriterStage className="absolute inset-0" />

          {/* Natural document flow: no forced viewport height, so a tall column
              scrolls instead of being center-overflowed into overlapping. */}
          <section
            aria-labelledby="writer-guest-heading"
            className="relative mx-auto flex w-full max-w-3xl flex-col items-center px-6 pt-24 pb-20 text-center md:pt-32 md:pb-24"
          >
            <motion.h1
              {...rise(0)}
              id="writer-guest-heading"
              className="mb-4 text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl"
            >
              What are you writing?
            </motion.h1>
            <motion.p
              {...rise(0.04)}
              className="mb-9 max-w-[52ch] text-base text-muted-foreground sm:text-lg"
            >
              Describe it and the agent drafts it in LaTeX, compiles it, and shows you the result.
            </motion.p>

            {/* The last thing to settle is the thing we want used */}
            <motion.div {...rise(0.14)} className="w-full text-left">
              <PromptComposer
                onSubmitPrompt={(prompt) => void submitPrompt(prompt)}
                onChipIntent={handleChipIntent}
                onActiveChange={setComposerActive}
              />
            </motion.div>

            {/* Mobile: one quiet line of results under the composer */}
            <motion.div {...rise(0.18)} className="mt-5 sm:hidden">
              <ManuscriptProof variant="strip" intent={chipIntent} paused={composerActive} />
            </motion.div>

            <motion.ul
              {...rise(0.2)}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-3"
            >
              {CAPABILITIES.map((c) => (
                <li key={c.title} className="flex items-center gap-2">
                  <c.icon className="h-4 w-4 shrink-0 text-primary" />
                  <h2 className="text-sm font-medium text-foreground/85">{c.title}</h2>
                </li>
              ))}
            </motion.ul>

            <motion.p {...rise(0.24)} className="mt-4 text-xs text-muted-foreground">
              Your draft stays private until you publish · Written and processed in the EU · Free to
              start
            </motion.p>

            {/* The quietest element on the page, desktop only: the composer
                keeps first-screen dominance, this merely invites the curious
                to watch the proof assemble from the start. */}
            <div className="mt-4 hidden sm:block">
              <AnimatePresence>
                {!hintDismissed && <SeeHowItWorks onClick={handleSeeHowItWorks} />}
              </AnimatePresence>
            </div>

            {/* The manuscript assembling below the trust line, with its own
                breathing room. Positive margin only — never overlap the line
                that carries the privacy promise. */}
            <motion.div
              {...rise(0.3)}
              ref={proofRef}
              aria-hidden="true"
              className="pointer-events-none mt-10 hidden w-full max-w-xl opacity-90 sm:block"
            >
              <ManuscriptProof intent={chipIntent} paused={composerActive} replayToken={replayToken} />
            </motion.div>
          </section>
        </main>
        <Footer />
      </div>

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onAuthSuccess={continueAfterAuth}
        message="Create a free account to start writing. Your prompt is saved and will run right after sign-in."
        onBeforeGoogleAuth={preserveOAuthDestination}
      />
    </>
  );
};

export default WriterGuestLanding;
